import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, Tabs, Tag, Popconfirm, message } from "antd";
import { ArrowLeft, Plus, Trash2, Pencil, CheckCircle } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  useVehicleList, useVehicleCreate, useVehicleUpdate, useVehicleDelete,
  useDefectList, useDefectResolve,
  useMileageList,
} from "../../../api-hooks/vehicles";

const FUEL_COLOR: Record<string, string> = { petrol: "red", diesel: "default", electric: "green", hybrid: "blue" };
const SEV_COLOR: Record<string, string> = { minor: "orange", major: "volcano", dangerous: "red" };

export default function FleetPage() {
  const { companyId: cParam } = useParams<{ companyId: string }>();
  const cid = Number(cParam);
  const navigate = useNavigate();
  const [msgApi, ctx] = message.useMessage();

  // Vehicles
  const { data: vehicleResult } = useVehicleList(cid);
  const vehicles: any[] = vehicleResult?.data ?? [];
  const [vModal, setVModal] = useState<{ open: boolean; record: any | null }>({ open: false, record: null });
  const [vForm] = Form.useForm();
  const createV = useVehicleCreate(cid, { onSuccess: () => { msgApi.success("Vehicle saved."); setVModal({ open: false, record: null }); vForm.resetFields(); }, onError: (e) => msgApi.error(e?.message || "Error") });
  const updateV = useVehicleUpdate(cid, { onSuccess: () => { msgApi.success("Updated."); setVModal({ open: false, record: null }); vForm.resetFields(); }, onError: (e) => msgApi.error(e?.message || "Error") });
  const deleteV = useVehicleDelete(cid, { onSuccess: () => msgApi.success("Deleted.") });

  // Defects
  const { data: defects = [] } = useDefectList(cid);
  const resolveD = useDefectResolve(cid, { onSuccess: () => msgApi.success("Defect resolved.") });

  // Mileage
  const { data: mileageResult } = useMileageList(cid);
  const mileage: any[] = mileageResult?.data ?? [];

  const vehicleCols = [
    { title: "Reg", dataIndex: "registration", key: "registration", render: (v: string) => <span className="font-mono font-bold">{v}</span> },
    { title: "Make", dataIndex: "make", key: "make", render: (v: string) => v || "—" },
    { title: "Model", dataIndex: "model", key: "model", render: (v: string) => v || "—" },
    { title: "Year", dataIndex: "year", key: "year" },
    { title: "Fuel", dataIndex: "fuel_type", key: "fuel_type", render: (v: string) => <Tag color={FUEL_COLOR[v]}>{v}</Tag> },
    { title: "MOT Due", dataIndex: "mot_due", key: "mot_due", render: (v: string) => v ? dayjs(v).format("DD/MM/YYYY") : "—" },
    { title: "Service Due", dataIndex: "service_due", key: "service_due", render: (v: string) => v ? dayjs(v).format("DD/MM/YYYY") : "—" },
    { title: "Status", dataIndex: "is_active", key: "is_active", render: (v: number) => <Tag color={v ? "green" : "red"}>{v ? "Active" : "Inactive"}</Tag> },
    {
      title: "", key: "actions", dataIndex: "id", render: (_: any, r: any) => (
        <div className="flex gap-2">
          <Button size="small" icon={<Pencil size={13} />} onClick={() => {
            setVModal({ open: true, record: r });
            vForm.setFieldsValue({ ...r, mot_due: r.mot_due ? dayjs(r.mot_due) : null, tax_due: r.tax_due ? dayjs(r.tax_due) : null, insurance_due: r.insurance_due ? dayjs(r.insurance_due) : null, service_due: r.service_due ? dayjs(r.service_due) : null });
          }} />
          <Popconfirm title="Delete vehicle?" onConfirm={() => deleteV.mutate(r.id)}><Button size="small" danger icon={<Trash2 size={13} />} /></Popconfirm>
        </div>
      ),
    },
  ];

  const defectCols = [
    { title: "Vehicle", dataIndex: "vehicle_registration", key: "vehicle_registration", render: (v: string) => <span className="font-mono font-bold">{v}</span> },
    { title: "Area", dataIndex: "defect_area", key: "defect_area" },
    { title: "Description", dataIndex: "description", key: "description" },
    { title: "Severity", dataIndex: "severity", key: "severity", render: (v: string) => <Tag color={SEV_COLOR[v]}>{v}</Tag> },
    { title: "Reported", dataIndex: "created_at", key: "created_at", render: (v: string) => dayjs(v).format("DD MMM YYYY") },
    { title: "Status", dataIndex: "is_resolved", key: "is_resolved", render: (v: number) => <Tag color={v ? "green" : "orange"}>{v ? "Resolved" : "Open"}</Tag> },
    {
      title: "", key: "actions", dataIndex: "id", render: (_: any, r: any) => r.is_resolved ? null : (
        <Popconfirm title="Mark as resolved?" onConfirm={() => resolveD.mutate(r.id)}>
          <Button size="small" icon={<CheckCircle size={13} />} type="primary">Resolve</Button>
        </Popconfirm>
      ),
    },
  ];

  const mileageCols = [
    { title: "Date", dataIndex: "log_date", key: "log_date", render: (v: string) => dayjs(v).format("DD MMM YYYY") },
    { title: "Vehicle", dataIndex: "vehicle_registration", key: "vehicle_registration", render: (v: string) => <span className="font-mono font-bold">{v}</span> },
    { title: "Driver", dataIndex: "user_name", key: "user_name", render: (v: string) => v || "—" },
    { title: "Start", dataIndex: "start_odometer", key: "start_odometer", render: (v: number) => `${v} mi` },
    { title: "End", dataIndex: "end_odometer", key: "end_odometer", render: (v: number) => `${v} mi` },
    { title: "Distance", dataIndex: "distance_miles", key: "distance_miles", render: (v: number) => <span className="font-semibold">{v} mi</span> },
    { title: "Type", dataIndex: "journey_type", key: "journey_type", render: (v: string) => <Tag color={v === "business" ? "blue" : "default"}>{v}</Tag> },
    { title: "From", dataIndex: "from_location", key: "from_location", render: (v: string) => v || "—" },
    { title: "To", dataIndex: "to_location", key: "to_location", render: (v: string) => v || "—" },
  ];

  const tabItems = [
    {
      key: "vehicles", label: `Vehicles (${vehicles.length})`,
      children: (
        <div>
          <div className="flex justify-between mb-3">
            <span className="text-sm text-slate-500">Fleet vehicles</span>
            <Button type="primary" icon={<Plus size={14} />} onClick={() => setVModal({ open: true, record: null })}>Add Vehicle</Button>
          </div>
          <Table dataSource={vehicles} columns={vehicleCols} rowKey="id" pagination={{ pageSize: 20 }} scroll={{ x: "max-content" }} size="small" />
        </div>
      ),
    },
    {
      key: "defects", label: `Defects (${defects.length})`,
      children: <Table dataSource={defects} columns={defectCols} rowKey="id" pagination={{ pageSize: 20 }} scroll={{ x: "max-content" }} size="small" />,
    },
    {
      key: "mileage", label: `Mileage Logs (${mileage.length})`,
      children: <Table dataSource={mileage} columns={mileageCols} rowKey="id" pagination={{ pageSize: 20 }} scroll={{ x: "max-content" }} size="small" />,
    },
  ];

  return (
    <div className="p-6">
      {ctx}
      <div className="flex items-center gap-3 mb-6">
        <Button icon={<ArrowLeft size={16} />} onClick={() => navigate(-1)} />
        <div>
          <h1 className="text-xl font-semibold text-slate-800 mb-0">Fleet Management</h1>
          <p className="text-slate-500 text-sm">Company ID: {cid}</p>
        </div>
      </div>

      <Tabs items={tabItems} />

      {/* Vehicle Modal */}
      <Modal
        title={vModal.record ? "Edit Vehicle" : "Add Vehicle"}
        open={vModal.open}
        onCancel={() => { setVModal({ open: false, record: null }); vForm.resetFields(); }}
        footer={null}
        width={640}
      >
        <Form form={vForm} layout="vertical" onFinish={(vals) => {
          const payload = {
            ...vals,
            mot_due: vals.mot_due ? vals.mot_due.format("YYYY-MM-DD") : undefined,
            tax_due: vals.tax_due ? vals.tax_due.format("YYYY-MM-DD") : undefined,
            insurance_due: vals.insurance_due ? vals.insurance_due.format("YYYY-MM-DD") : undefined,
            service_due: vals.service_due ? vals.service_due.format("YYYY-MM-DD") : undefined,
          };
          if (vModal.record) updateV.mutate({ ...payload, id: vModal.record.id });
          else createV.mutate(payload);
        }} className="mt-4">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="registration" label="Registration" rules={[{ required: true }]}><Input className="uppercase" /></Form.Item>
            <Form.Item name="make" label="Make"><Input /></Form.Item>
            <Form.Item name="model" label="Model"><Input /></Form.Item>
            <Form.Item name="year" label="Year"><Input type="number" /></Form.Item>
            <Form.Item name="colour" label="Colour"><Input /></Form.Item>
            <Form.Item name="fuel_type" label="Fuel Type" initialValue="diesel">
              <Select options={[{ label: "Diesel", value: "diesel" }, { label: "Petrol", value: "petrol" }, { label: "Electric", value: "electric" }, { label: "Hybrid", value: "hybrid" }]} />
            </Form.Item>
            <Form.Item name="mot_due" label="MOT Due"><DatePicker className="w-full" /></Form.Item>
            <Form.Item name="tax_due" label="Tax Due"><DatePicker className="w-full" /></Form.Item>
            <Form.Item name="insurance_due" label="Insurance Due"><DatePicker className="w-full" /></Form.Item>
            <Form.Item name="service_due" label="Service Due"><DatePicker className="w-full" /></Form.Item>
          </div>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => { setVModal({ open: false, record: null }); vForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createV.isPending || updateV.isPending}>Save</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
